class ProductInfoTabs {
  constructor() {
    this.nav = document.querySelector('.product-info-tabs__nav');
    this.navItems = document.querySelectorAll('.product-info-tabs__nav-item');
    this.sections = document.querySelectorAll('.product-info-tabs__section');
    this.scrollOffset = parseInt(this.nav?.dataset.scrollOffset) || 100;
    this.isScrolling = false;
    
    if (this.nav && this.sections.length > 0) {
      this.init();
    }
  }

  init() {
    this.setupStickyNav();
    this.setupNavigation();
    this.setupScrollSpy();
    this.setupSmoothScroll();
  }

  setupStickyNav() {
    if (!this.nav) return;
    
    const navWrapper = this.nav.parentElement;
    const sectionContainer = document.querySelector('.product-info-tabs');
    
    // Observer for the navigation wrapper (when it goes out of view)
    const navObserver = new IntersectionObserver(
      ([entry]) => {
        // Only make sticky if we're still within the main section
        const sectionRect = sectionContainer.getBoundingClientRect();
        const isWithinSection = sectionRect.top < window.innerHeight && sectionRect.bottom > 0;
        
        if (!entry.isIntersecting && isWithinSection) {
          this.nav.classList.add('product-info-tabs__nav--sticky');
          // Add padding to prevent jump
          document.body.style.paddingTop = `${this.nav.offsetHeight}px`;
        } else {
          this.nav.classList.remove('product-info-tabs__nav--sticky');
          document.body.style.paddingTop = '0';
        }
      },
      { 
        rootMargin: '0px 0px -1px 0px',
        threshold: 0 
      }
    );
    
    // Observer for the main section (when section goes out of view, remove sticky)
    const sectionObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) {
          // Section is out of view, remove sticky nav
          this.nav.classList.remove('product-info-tabs__nav--sticky');
          document.body.style.paddingTop = '0';
        }
      },
      { 
        rootMargin: '0px 0px -50px 0px', // Remove sticky when section is mostly out of view
        threshold: 0 
      }
    );
    
    navObserver.observe(navWrapper);
    sectionObserver.observe(sectionContainer);
  }

  setupNavigation() {
    this.navItems.forEach(item => {
      item.addEventListener('click', (e) => {
        e.preventDefault();
        const targetId = item.getAttribute('href').substring(1);
        const targetSection = document.getElementById(targetId);
        
        if (targetSection) {
          this.scrollToSection(targetSection);
        }
      });
    });
  }

  setupScrollSpy() {
    const observerOptions = {
      root: null,
      rootMargin: `-${this.scrollOffset}px 0px -50% 0px`,
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      if (this.isScrolling) return;
      
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.setActiveNav(entry.target.id);
        }
      });
    }, observerOptions);

    this.sections.forEach(section => {
      observer.observe(section);
    });
  }

  setupSmoothScroll() {
    // Handle browser back/forward navigation
    window.addEventListener('hashchange', () => {
      const hash = window.location.hash.substring(1);
      const targetSection = document.getElementById(hash);
      
      if (targetSection && this.sections.length > 0) {
        this.scrollToSection(targetSection);
      }
    });

    // Handle initial page load with hash
    if (window.location.hash) {
      setTimeout(() => {
        const hash = window.location.hash.substring(1);
        const targetSection = document.getElementById(hash);
        
        if (targetSection) {
          this.scrollToSection(targetSection);
        }
      }, 100);
    }
  }

  scrollToSection(targetSection) {
    if (!targetSection) return;
    
    this.isScrolling = true;
    
    const offsetTop = targetSection.offsetTop - this.scrollOffset;
    
    window.scrollTo({
      top: offsetTop,
      behavior: 'smooth'
    });
    
    // Update URL hash
    history.pushState(null, null, `#${targetSection.id}`);
    
    // Set active navigation
    this.setActiveNav(targetSection.id);
    
    // Reset scrolling flag after animation
    setTimeout(() => {
      this.isScrolling = false;
    }, 1000);
  }

  setActiveNav(sectionId) {
    this.navItems.forEach(item => {
      const href = item.getAttribute('href').substring(1);
      if (href === sectionId) {
        item.classList.add('product-info-tabs__nav-item--active');
      } else {
        item.classList.remove('product-info-tabs__nav-item--active');
      }
    });
  }
}

// Form Enhancement
class ProductInquiryForm {
  constructor() {
    this.form = document.querySelector('.product-info-tabs__form');
    this.submitButton = document.querySelector('.product-info-tabs__form-submit');
    
    if (this.form) {
      this.init();
    }
  }

  init() {
    this.setupFormValidation();
    this.setupFormSubmission();
  }

  setupFormValidation() {
    const inputs = this.form.querySelectorAll('input[required], textarea[required]');
    
    inputs.forEach(input => {
      input.addEventListener('blur', () => {
        this.validateField(input);
      });
      
      input.addEventListener('input', () => {
        if (input.classList.contains('error')) {
          this.validateField(input);
        }
      });
    });
  }

  validateField(field) {
    const isValid = field.checkValidity();
    
    if (!isValid) {
      field.classList.add('error');
      field.style.borderColor = '#e74c3c';
    } else {
      field.classList.remove('error');
      field.style.borderColor = '#e8e8e8';
    }
    
    return isValid;
  }

  setupFormSubmission() {
    this.form.addEventListener('submit', (e) => {
      e.preventDefault();
      
      // Validate all fields
      const inputs = this.form.querySelectorAll('input[required], textarea[required]');
      let allValid = true;
      
      inputs.forEach(input => {
        if (!this.validateField(input)) {
          allValid = false;
        }
      });
      
      if (!allValid) {
        this.showMessage('Please fill in all required fields correctly.', 'error');
        return;
      }
      
      this.submitForm();
    });
  }

  async submitForm() {
    const formData = new FormData(this.form);
    const originalButtonText = this.submitButton.textContent;
    
    // Show loading state
    this.submitButton.textContent = 'Sending...';
    this.submitButton.disabled = true;
    
    try {
      // For now, we'll simulate form submission
      // In a real implementation, you'd send this to your backend or email service
      await this.simulateFormSubmission(formData);
      
      this.showMessage('Thank you! Your inquiry has been sent successfully.', 'success');
      this.form.reset();
      
    } catch (error) {
      console.error('Form submission error:', error);
      this.showMessage('Sorry, there was an error sending your inquiry. Please try again.', 'error');
    } finally {
      // Reset button
      this.submitButton.textContent = originalButtonText;
      this.submitButton.disabled = false;
    }
  }

  simulateFormSubmission(formData) {
    return new Promise((resolve) => {
      // Simulate API call delay
      setTimeout(() => {
        console.log('Form submission data:', Object.fromEntries(formData));
        resolve();
      }, 1500);
    });
  }

  showMessage(message, type) {
    // Remove existing messages
    const existingMessages = this.form.querySelectorAll('.form-message');
    existingMessages.forEach(msg => msg.remove());
    
    // Create new message
    const messageDiv = document.createElement('div');
    messageDiv.className = `form-message form-message--${type}`;
    messageDiv.textContent = message;
    messageDiv.style.cssText = `
      padding: 1rem 1.5rem;
      margin: 1rem 0;
      border-radius: 8px;
      font-size: 1.4rem;
      ${type === 'success' 
        ? 'background-color: #d4edda; color: #155724; border: 1px solid #c3e6cb;' 
        : 'background-color: #f8d7da; color: #721c24; border: 1px solid #f5c6cb;'
      }
    `;
    
    this.form.appendChild(messageDiv);
    
    // Auto-remove success messages
    if (type === 'success') {
      setTimeout(() => {
        messageDiv.remove();
      }, 5000);
    }
  }
}

// Shopping Guide Enhancement
class ShoppingGuide {
  constructor() {
    this.guideItems = document.querySelectorAll('.product-info-tabs__guide-item');
    
    if (this.guideItems.length > 0) {
      this.init();
    }
  }

  init() {
    this.setupAccordionBehavior();
  }

  setupAccordionBehavior() {
    this.guideItems.forEach(item => {
      const summary = item.querySelector('.product-info-tabs__guide-summary');
      
      summary.addEventListener('click', (e) => {
        // Optional: Close other items when opening one (accordion behavior)
        // Uncomment the lines below if you want only one item open at a time
        /*
        if (!item.hasAttribute('open')) {
          this.guideItems.forEach(otherItem => {
            if (otherItem !== item) {
              otherItem.removeAttribute('open');
            }
          });
        }
        */
      });
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener('DOMContentLoaded', () => {
  new ProductInfoTabs();
  new ProductInquiryForm();
  new ShoppingGuide();
});

// Handle page resize
window.addEventListener('resize', () => {
  // Recalculate sticky nav positioning if needed
  const nav = document.querySelector('.product-info-tabs__nav--sticky');
  if (nav) {
    document.body.style.paddingTop = `${nav.offsetHeight}px`;
  }
});